import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const BudgetSelector = ({ selectedBudget, onBudgetChange, error }) => {
  const [customAmount, setCustomAmount] = useState('');

  const currencies = [
    { code: 'GBP', symbol: '£', name: 'British Pound' },
    { code: 'USD', symbol: '$', name: 'US Dollar' },
    { code: 'EUR', symbol: '€', name: 'Euro' },
    { code: 'AUD', symbol: 'A$', name: 'Australian Dollar' },
    { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar' },
    { code: 'NZD', symbol: 'NZ$', name: 'New Zealand Dollar' }
  ];

  const budgetRanges = [
    { id: 'no-budget', min: null, max: null, label: 'Do not specify budget', description: 'Let agents propose options within their expertise' },
    { id: 'budget', min: 500, max: 1500, label: 'Budget', description: 'Essential travel experience' },
    { id: 'mid-range', min: 1500, max: 3500, label: 'Mid-Range', description: 'Comfortable travel with good amenities' },
    { id: 'luxury', min: 3500, max: 7500, label: 'Luxury', description: 'Premium experience with top amenities' },
    { id: 'ultra-luxury', min: 7500, max: null, label: 'Ultra Luxury', description: 'Exclusive and bespoke experiences' },
    { id: 'custom', min: null, max: null, label: 'Custom Amount', description: 'Specify your own budget' }
  ];

  const handleCurrencyChange = (currency) => {
    onBudgetChange({
      ...selectedBudget,
      currency: currency?.code,
      symbol: currency?.symbol
    });
  };

  const handleRangeChange = (range) => {
    if (range?.id === 'no-budget') {
      onBudgetChange({
        ...selectedBudget,
        type: 'no-budget',
        range: 'no-budget',
        amount: null,
        min: null,
        max: null
      });
    } else if (range?.id === 'custom') {
      onBudgetChange({
        ...selectedBudget,
        type: 'custom',
        amount: customAmount ? parseFloat(customAmount) : null,
        min: null,
        max: null
      });
    } else {
      onBudgetChange({
        ...selectedBudget,
        type: 'range',
        range: range?.id,
        min: range?.min,
        max: range?.max,
        amount: null
      });
    }
  };

  const handleCustomAmountChange = (value) => {
    setCustomAmount(value);
    if (selectedBudget?.type === 'custom') {
      onBudgetChange({
        ...selectedBudget,
        amount: value ? parseFloat(value) : null
      });
    }
  };

  const formatAmount = (amount) => {
    if (!amount) return '';
    return new Intl.NumberFormat('en-GB', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    })?.format(amount);
  };

  const getBudgetDisplay = () => {
    if (!selectedBudget) return '';
    
    if (selectedBudget?.type === 'no-budget') {
      return 'Budget not specified - agents will provide various options';
    }
    
    const symbol = selectedBudget?.symbol || '£';
    
    if (selectedBudget?.type === 'custom' && selectedBudget?.amount) {
      return `${symbol}${formatAmount(selectedBudget?.amount)}`;
    }
    
    if (selectedBudget?.type === 'range' && selectedBudget?.min) {
      if (selectedBudget?.max) {
        return `${symbol}${formatAmount(selectedBudget?.min)} - ${symbol}${formatAmount(selectedBudget?.max)}`;
      } else {
        return `${symbol}${formatAmount(selectedBudget?.min)}+`;
      }
    }
    
    return '';
  };

  // Show currency selection only when budget is specified
  const showCurrencySelection = selectedBudget?.type !== 'no-budget';

  return (
    <div className="space-y-6">
      {/* Currency Selection - only show when budget is being specified */}
      {showCurrencySelection && (
        <div>
          <h3 className="text-lg font-semibold text-foreground mb-4">Select Currency</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {currencies?.map((currency) => (
              <button
                key={currency?.code}
                onClick={() => handleCurrencyChange(currency)}
                className={`p-3 rounded-lg border-2 text-center transition-all hover:scale-105 ${
                  selectedBudget?.currency === currency?.code
                    ? 'border-primary bg-primary/5 shadow-md'
                    : 'border-border hover:border-primary/50 hover:bg-muted/50'
                }`}
              >
                <div className={`text-lg font-bold ${
                  selectedBudget?.currency === currency?.code ? 'text-primary' : 'text-foreground'
                }`}>
                  {currency?.symbol}
                </div>
                <div className={`text-xs font-medium ${
                  selectedBudget?.currency === currency?.code ? 'text-primary' : 'text-muted-foreground'
                }`}>
                  {currency?.code}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Budget Range Selection */}
      <div>
        <h3 className="text-lg font-semibold text-foreground mb-4">What's your budget per person?</h3>
        <div className="space-y-3">
          {budgetRanges?.map((range) => (
            <button
              key={range?.id}
              onClick={() => handleRangeChange(range)}
              className={`w-full p-4 rounded-lg border-2 text-left transition-all hover:scale-102 ${
                (selectedBudget?.range === range?.id) || (selectedBudget?.type === 'custom' && range?.id === 'custom') || (selectedBudget?.type === 'no-budget' && range?.id === 'no-budget')
                  ? 'border-primary bg-primary/5 shadow-md' :'border-border hover:border-primary/50 hover:bg-muted/50'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3">
                    <div className={`p-2 rounded-lg ${
                      (selectedBudget?.range === range?.id) || (selectedBudget?.type === 'custom' && range?.id === 'custom') || (selectedBudget?.type === 'no-budget' && range?.id === 'no-budget')
                        ? 'bg-primary text-primary-foreground' :'bg-muted text-muted-foreground'
                    }`}>
                      <Icon name={
                        range?.id === 'no-budget' ? 'HelpCircle' : 
                        range?.id === 'custom' ? 'Edit3' : 'Banknote'
                      } size={16} />
                    </div>
                    <div>
                      <h4 className={`font-medium ${
                        (selectedBudget?.range === range?.id) || (selectedBudget?.type === 'custom' && range?.id === 'custom') || (selectedBudget?.type === 'no-budget' && range?.id === 'no-budget')
                          ? 'text-primary' :'text-foreground'
                      }`}>
                        {range?.label}
                      </h4>
                      <p className="text-sm text-muted-foreground">
                        {range?.description}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  {range?.id !== 'custom' && range?.id !== 'no-budget' && (
                    <div className={`font-semibold ${
                      selectedBudget?.range === range?.id ? 'text-primary' : 'text-foreground'
                    }`}>
                      {selectedBudget?.symbol || '£'}{formatAmount(range?.min)}
                      {range?.max ? ` - ${selectedBudget?.symbol || '£'}${formatAmount(range?.max)}` : '+'}
                    </div>
                  )}
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Custom Amount Input */}
      {selectedBudget?.type === 'custom' && (
        <div className="bg-muted/30 p-6 rounded-lg">
          <h4 className="font-medium text-foreground mb-4">Enter your budget amount</h4>
          <div className="relative">
            <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-foreground font-medium">
              {selectedBudget?.symbol || '£'}
            </span>
            <input
              type="number"
              placeholder="0"
              value={customAmount}
              onChange={(e) => handleCustomAmountChange(e?.target?.value)}
              className="w-full pl-8 pr-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-lg font-medium"
              min="0"
              step="50"
            />
          </div>
          <p className="text-sm text-muted-foreground mt-2">
            Enter the total budget per person for your trip
          </p>
        </div>
      )}

      {/* Budget Summary */}
      {getBudgetDisplay() && (
        <div className={`p-4 rounded-lg ${
          selectedBudget?.type === 'no-budget' ?'bg-muted/20 border border-muted' :'bg-primary/10'
        }`}>
          <div className="flex items-center space-x-2">
            <Icon name={
              selectedBudget?.type === 'no-budget' ? 'HelpCircle' : 'Banknote'
            } size={20} className={
              selectedBudget?.type === 'no-budget' ? 'text-muted-foreground' : 'text-primary'
            } />
            <span className={`font-semibold ${
              selectedBudget?.type === 'no-budget' ?'text-muted-foreground' :'text-primary'
            }`}>
              {selectedBudget?.type === 'no-budget' 
                ? getBudgetDisplay()
                : `Budget: ${getBudgetDisplay()} per person`
              }
            </span>
          </div>
        </div>
      )}

      {error && (
        <p className="text-error text-sm">{error}</p>
      )}
    </div>
  );
};

export default BudgetSelector;